import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useMemo, useState } from "react";
import { AppShell } from "@/components/app-shell";
import { requireRole } from "@/lib/session";
import { useTeacherContext, formatStreamLabel } from "@/lib/teacher-context";
import {
  getTeacherIntelligence,
  assignPracticeFromInsight,
  type TeacherIntelligence,
} from "@/lib/teacher-intelligence.functions";
import {
  Users, TrendingUp, TrendingDown, AlertTriangle, Sparkles, Brain,
  ClipboardList, Download, Loader2, ChevronRight, Grid3x3, Target,
  FileText, Flame,
} from "lucide-react";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/teacher/intelligence")({
  ssr: false,
  beforeLoad: () => requireRole("teacher"),
  head: () => ({
    meta: [
      { title: "AI Intelligence Centre — MAKAI" },
      { name: "description", content: "AI Teacher Intelligence Centre: class heat maps, hardest topics, intervention groups, and teaching recommendations." },
      { property: "og:title", content: "AI Teacher Intelligence Centre — MAKAI" },
      { property: "og:description", content: "Turn class data into next actions: hardest topics, at-risk learners, and AI teaching recommendations." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: TeacherIntelligenceCentre,
});

function TeacherIntelligenceCentre() {
  const ctx = useTeacherContext();
  const [streamId, setStreamId] = useState<string>("");
  const [subjectId, setSubjectId] = useState<string>("");
  const [data, setData] = useState<TeacherIntelligence | null>(null);
  const [loading, setLoading] = useState(false);
  const [assigning, setAssigning] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const fetchIntel = useServerFn(getTeacherIntelligence);
  const assign = useServerFn(assignPracticeFromInsight);

  useEffect(() => {
    if (!ctx.loading && !streamId && ctx.streams[0]) setStreamId(ctx.streams[0].id);
  }, [ctx.loading, ctx.streams, streamId]);

  useEffect(() => {
    if (!streamId) return;
    let cancelled = false;
    setLoading(true);
    fetchIntel({ data: { streamId, subjectId: subjectId || null } })
      .then((d) => { if (!cancelled) setData(d); })
      .catch((e) => { if (!cancelled) { setData(null); setToast(String((e as Error).message)); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [streamId, subjectId, fetchIntel]);

  async function handleAssign(payload: { title: string; instructions: string; subjectId?: string | null }) {
    if (!streamId) return;
    setAssigning(payload.title);
    try {
      await assign({
        data: {
          streamId,
          subjectId: payload.subjectId ?? subjectId ?? null,
          title: payload.title,
          instructions: payload.instructions,
          dueInDays: 3,
        },
      });
      setToast(`Assigned: ${payload.title}`);
    } catch (e) {
      setToast(String((e as Error).message));
    } finally {
      setAssigning(null);
      setTimeout(() => setToast(null), 4000);
    }
  }

  function downloadCsvReport() {
    if (!data) return;
    const rows: string[][] = [];
    rows.push(["Section", "Item", "Detail 1", "Detail 2", "Detail 3"]);
    rows.push(["Scope", data.scope.streamLabel, `${data.scope.learnerCount} learners`, data.scope.subjectLabel ?? "All subjects", new Date(data.scope.generatedAt).toLocaleString()]);
    rows.push(["Overview", "On track", String(data.overview.onTrack), "", ""]);
    rows.push(["Overview", "Developing", String(data.overview.developing), "", ""]);
    rows.push(["Overview", "Needs support", String(data.overview.needsSupport), "", ""]);
    rows.push(["Overview", "At risk", String(data.overview.atRisk), "", ""]);
    rows.push(["Overview", "Average mastery %", String(data.overview.averageMastery), "", ""]);
    rows.push(["Overview", "Active this week", String(data.overview.activeThisWeek), "", ""]);
    for (const t of data.hardestTopics) rows.push(["Hardest topic", t.label, `avg ${t.averageMastery}%`, `${t.affected} affected`, t.dominantError]);
    for (const m of data.mostImproved) rows.push(["Most improved", m.name, m.topic ?? "", `${m.fromPct}% → ${m.toPct}%`, `+${m.delta}%`]);
    for (const a of data.atRisk) rows.push(["At risk", a.name, a.reason, a.weakestTopic ?? "", `${a.activityLast7} activities`]);
    for (const g of data.interventionGroups) rows.push(["Intervention group", g.title, g.reason, `${g.learners.length} learners`, g.recommendedAction]);
    for (const q of data.questionAnalysis) rows.push(["Hard question", q.stem, q.quizTitle, `${q.correctPct}% correct`, `${q.attempts} attempts`]);
    for (const m of data.commonMistakes) rows.push(["Common mistake", `${m.kind} in ${m.label}`, `${m.learnersAffected} learners`, `${m.count} occurrences`, m.example ?? ""]);
    if (data.aiRecommendations.summary) rows.push(["AI summary", data.aiRecommendations.summary, "", "", ""]);
    for (const a of data.aiRecommendations.actions) rows.push(["AI action", a, "", "", ""]);
    const csv = rows.map((r) => r.map(csvCell).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `intelligence-${data.scope.streamLabel.replace(/\s+/g, "-")}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <AppShell role="teacher" title="AI Intelligence Centre" subtitle="Turn class data into the next teaching action.">
      <div className="mx-auto max-w-6xl space-y-6 print:max-w-none">
        {/* Scope controls */}
        <div className="flex flex-wrap items-center gap-3 print:hidden">
          <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Class</label>
          <select
            value={streamId}
            onChange={(e) => setStreamId(e.target.value)}
            className="rounded-lg border border-border bg-card px-3 py-1.5 text-sm"
          >
            {ctx.streams.map((s) => (
              <option key={s.id} value={s.id}>{formatStreamLabel({ class_name: s.class_name, name: s.name })}</option>
            ))}
          </select>
          <label className="ml-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Subject</label>
          <select
            value={subjectId}
            onChange={(e) => setSubjectId(e.target.value)}
            className="rounded-lg border border-border bg-card px-3 py-1.5 text-sm"
          >
            <option value="">All subjects</option>
            {ctx.subjects.map((s) => (<option key={s.id} value={s.id}>{s.name}</option>))}
          </select>
          <div className="ml-auto flex gap-2">
            <button
              onClick={downloadCsvReport}
              disabled={!data}
              className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-3 py-1.5 text-sm hover:bg-muted disabled:opacity-50"
            >
              <Download className="h-3.5 w-3.5" /> Download CSV
            </button>
            <button
              onClick={() => window.print()}
              disabled={!data}
              className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-3 py-1.5 text-sm hover:bg-muted disabled:opacity-50"
            >
              <FileText className="h-3.5 w-3.5" /> Print report
            </button>
          </div>
        </div>

        {toast && (
          <div className="rounded-lg border border-primary/30 bg-primary-soft/40 px-3 py-2 text-sm text-primary">
            {toast}
          </div>
        )}

        {loading && !data && (
          <div className="grid place-items-center rounded-2xl border border-border bg-card p-10 shadow-soft">
            <Loader2 className="h-5 w-5 animate-spin text-primary" />
          </div>
        )}

        {data && (
          <>
            {/* Class Overview */}
            <Section title="Class overview" icon={Users}>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-6">
                <Stat label="Learners" value={String(data.scope.learnerCount)} />
                <Stat label="On track" value={String(data.overview.onTrack)} tone="good" />
                <Stat label="Developing" value={String(data.overview.developing)} />
                <Stat label="Needs support" value={String(data.overview.needsSupport)} tone="warn" />
                <Stat label="At risk" value={String(data.overview.atRisk)} tone="bad" />
                <Stat label="Active this week" value={String(data.overview.activeThisWeek)} />
              </div>
              <div className="mt-4 text-sm text-muted-foreground">
                Average class mastery: <span className="font-semibold text-foreground">{data.overview.averageMastery}%</span>
              </div>
            </Section>

            {/* AI Teaching Recommendations */}
            <Section title="Makai's teaching recommendations" icon={Brain} accent>
              {data.aiRecommendations.summary && (
                <p className="text-sm leading-relaxed">{data.aiRecommendations.summary}</p>
              )}
              {data.aiRecommendations.actions.length > 0 && (
                <ul className="mt-3 space-y-2 text-sm">
                  {data.aiRecommendations.actions.map((a, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <ChevronRight className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                      <span>{a}</span>
                    </li>
                  ))}
                </ul>
              )}
              {!data.aiRecommendations.summary && data.aiRecommendations.actions.length === 0 && (
                <p className="text-sm text-muted-foreground">
                  {data.aiRecommendations.error ?? "Not enough data yet — recommendations will appear as learners engage."}
                </p>
              )}
            </Section>

            {/* Heat Map */}
            <Section title="Learning heat map" icon={Grid3x3}>
              {data.heatMap.topics.length === 0 ? (
                <p className="text-sm text-muted-foreground">Not enough mastery data yet.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr>
                        <th className="sticky left-0 z-10 bg-card px-2 py-1 text-left font-semibold">Learner</th>
                        {data.heatMap.topics.map((t) => (
                          <th key={t.node_key} className="px-1 py-1 text-left font-normal text-muted-foreground">
                            <div className="max-w-[80px] truncate" title={t.label}>{t.label}</div>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {data.heatMap.learners.map((l) => (
                        <tr key={l.id} className="border-t border-border">
                          <td className="sticky left-0 z-10 bg-card px-2 py-1 font-medium">{l.name}</td>
                          {l.cells.map((c) => (
                            <td key={c.node_key} className="px-1 py-1">
                              <div
                                className={cn(
                                  "h-6 min-w-[32px] rounded text-center text-[10px] font-semibold leading-6",
                                  cellClass(c.mastery),
                                )}
                                title={c.mastery == null ? "No data" : `${c.mastery}% · ${c.signals} signals`}
                              >
                                {c.mastery == null ? "—" : c.mastery}
                              </div>
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <div className="mt-3 flex flex-wrap items-center gap-3 text-[11px] text-muted-foreground">
                <span className="inline-flex items-center gap-1"><span className="h-3 w-3 rounded bg-emerald-500/70" /> Strong 70+</span>
                <span className="inline-flex items-center gap-1"><span className="h-3 w-3 rounded bg-amber-400/70" /> Developing 50-69</span>
                <span className="inline-flex items-center gap-1"><span className="h-3 w-3 rounded bg-orange-500/70" /> Needs support 35-49</span>
                <span className="inline-flex items-center gap-1"><span className="h-3 w-3 rounded bg-red-500/70" /> At risk &lt;35</span>
              </div>
            </Section>

            {/* Hardest topics */}
            <Section title="Most difficult topics" icon={Flame}>
              {data.hardestTopics.length === 0 ? (
                <p className="text-sm text-muted-foreground">No difficult topics detected.</p>
              ) : (
                <ul className="space-y-2">
                  {data.hardestTopics.map((t) => (
                    <li key={t.node_key} className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border p-3 text-sm">
                      <div className="min-w-0 flex-1">
                        <div className="font-semibold">{t.label}</div>
                        <div className="text-xs text-muted-foreground">
                          Avg mastery {t.averageMastery}% · {t.affected} learners struggling · {t.dominantError} errors
                        </div>
                      </div>
                      <button
                        onClick={() => handleAssign({
                          title: `Practice: ${t.label}`,
                          instructions: t.dominantError === "concept"
                            ? `Short revision + 5 questions on ${t.label} concepts.`
                            : `15-minute accuracy practice on ${t.label}.`,
                        })}
                        disabled={assigning !== null}
                        className="shrink-0 rounded-lg bg-primary px-3 py-1 text-xs font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
                      >
                        {assigning === `Practice: ${t.label}` ? "…" : "Assign practice"}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </Section>

            <div className="grid gap-5 md:grid-cols-2">
              {/* Most improved */}
              <Section title="Most improved learners" icon={TrendingUp}>
                {data.mostImproved.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No standout improvements yet.</p>
                ) : (
                  <ul className="space-y-2 text-sm">
                    {data.mostImproved.map((m) => (
                      <li key={m.learnerId} className="rounded-lg border border-border p-3">
                        <div className="flex items-center justify-between">
                          <span className="font-semibold">{m.name}</span>
                          <span className="rounded-full bg-primary-soft px-2 py-0.5 text-[11px] font-bold text-primary">
                            +{m.delta}%
                          </span>
                        </div>
                        {m.topic && (
                          <div className="mt-1 text-xs text-muted-foreground">
                            {m.topic}: {m.fromPct}% → {m.toPct}%
                          </div>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </Section>

              {/* At risk */}
              <Section title="Learners at risk" icon={AlertTriangle}>
                {data.atRisk.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No learners flagged this week.</p>
                ) : (
                  <ul className="space-y-2 text-sm">
                    {data.atRisk.map((a) => (
                      <li key={a.learnerId} className="rounded-lg border border-border p-3">
                        <div className="font-semibold">{a.name}</div>
                        <div className="text-xs text-muted-foreground">{a.reason}</div>
                      </li>
                    ))}
                  </ul>
                )}
              </Section>
            </div>

            {/* Intervention groups */}
            <Section title="Suggested intervention groups" icon={Target}>
              {data.interventionGroups.length === 0 ? (
                <p className="text-sm text-muted-foreground">No intervention groups needed right now.</p>
              ) : (
                <ul className="space-y-3">
                  {data.interventionGroups.map((g) => (
                    <li key={g.id} className="rounded-lg border border-border p-3">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <div className="font-semibold">{g.title}</div>
                        <button
                          onClick={() => handleAssign({ title: g.title, instructions: g.recommendedAction })}
                          disabled={assigning !== null}
                          className="rounded-lg bg-primary px-3 py-1 text-xs font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
                        >
                          {assigning === g.title ? "…" : "Assign to class"}
                        </button>
                      </div>
                      <div className="mt-1 text-xs text-muted-foreground">{g.reason}</div>
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {g.learners.map((l) => (
                          <span key={l.id} className="rounded-full bg-muted px-2 py-0.5 text-[11px]">{l.name}</span>
                        ))}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </Section>

            {/* Assignment recommendations */}
            <Section title="Assignment recommendations" icon={ClipboardList}>
              {data.hardestTopics.length === 0 && data.interventionGroups.length === 0 ? (
                <p className="text-sm text-muted-foreground">No recommendations available yet.</p>
              ) : (
                <ul className="space-y-2 text-sm">
                  {data.hardestTopics.slice(0, 3).map((t) => (
                    <li key={`rec-${t.node_key}`} className="flex items-start justify-between gap-3 rounded-lg border border-border p-3">
                      <div>
                        <div className="font-semibold">Practice: {t.label}</div>
                        <div className="text-xs text-muted-foreground">
                          {t.dominantError === "concept" ? "Short concept revision + 5 questions." : "15-minute accuracy set."}
                        </div>
                      </div>
                      <button
                        onClick={() => handleAssign({
                          title: `Practice: ${t.label}`,
                          instructions: t.dominantError === "concept"
                            ? `Concept revision + 5 questions on ${t.label}.`
                            : `15-minute accuracy set on ${t.label}.`,
                        })}
                        disabled={assigning !== null}
                        className="shrink-0 rounded-lg border border-primary px-3 py-1 text-xs font-semibold text-primary hover:bg-primary-soft disabled:opacity-50"
                      >
                        Assign
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </Section>

            {/* Question analysis */}
            <Section title="Question analysis" icon={FileText}>
              {data.questionAnalysis.length === 0 ? (
                <p className="text-sm text-muted-foreground">Not enough quiz data yet.</p>
              ) : (
                <ul className="space-y-2 text-sm">
                  {data.questionAnalysis.map((q) => (
                    <li key={q.questionId} className="rounded-lg border border-border p-3">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <div className="font-medium">{q.stem || "(question)"}</div>
                          <div className="mt-0.5 text-xs text-muted-foreground">{q.quizTitle} · {q.attempts} attempts</div>
                        </div>
                        <span className={cn(
                          "shrink-0 rounded-full px-2 py-0.5 text-[11px] font-bold",
                          q.correctPct < 40 ? "bg-red-100 text-red-700"
                            : q.correctPct < 70 ? "bg-amber-100 text-amber-700"
                            : "bg-emerald-100 text-emerald-700",
                        )}>
                          {q.correctPct}%
                        </span>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </Section>

            {/* Smart working */}
            <Section title="Smart Working analysis" icon={Brain}>
              {data.smartWorking.totalSteps === 0 ? (
                <p className="text-sm text-muted-foreground">No Smart Working steps analysed yet.</p>
              ) : (
                <div className="space-y-3 text-sm">
                  <div className="grid grid-cols-3 gap-3">
                    <Stat label="Steps analysed" value={String(data.smartWorking.totalSteps)} />
                    <Stat label="Arithmetic errors" value={`${data.smartWorking.arithmeticErrorRate}%`} tone={data.smartWorking.arithmeticErrorRate > 20 ? "warn" : undefined} />
                    <Stat label="Concept errors" value={`${data.smartWorking.conceptErrorRate}%`} tone={data.smartWorking.conceptErrorRate > 20 ? "warn" : undefined} />
                  </div>
                  {data.smartWorking.topClasses.length > 0 && (
                    <div>
                      <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Top error classes</div>
                      <ul className="mt-2 flex flex-wrap gap-1.5">
                        {data.smartWorking.topClasses.map((c) => (
                          <li key={c.label} className="rounded-full bg-muted px-2 py-0.5 text-[11px]">
                            {c.label} · {c.count}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </Section>

            {/* Common mistakes */}
            <Section title="Common mistakes" icon={TrendingDown}>
              {data.commonMistakes.length === 0 ? (
                <p className="text-sm text-muted-foreground">No repeated mistakes detected.</p>
              ) : (
                <ul className="space-y-2 text-sm">
                  {data.commonMistakes.map((m, i) => (
                    <li key={i} className="rounded-lg border border-border p-3">
                      <div className="font-semibold">{m.kind} in {m.label}</div>
                      <div className="text-xs text-muted-foreground">
                        {m.learnersAffected} learners · {m.count} occurrences
                      </div>
                      {m.example && (
                        <div className="mt-1 text-xs italic text-muted-foreground">e.g. {m.example}</div>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </Section>

            <p className="pt-4 text-center text-[11px] text-muted-foreground">
              Generated {new Date(data.scope.generatedAt).toLocaleString()} · MAKAI Intelligence Centre
            </p>
          </>
        )}
      </div>
    </AppShell>
  );
}

function Section({
  title, icon: Icon, accent, children,
}: {
  title: string;
  icon: typeof Users;
  accent?: boolean;
  children: React.ReactNode;
}) {
  return (
    <section className={cn(
      "rounded-2xl border p-5 shadow-soft",
      accent ? "border-primary/20 bg-primary-soft/30" : "border-border bg-card",
    )}>
      <div className={cn(
        "flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.16em]",
        accent ? "text-primary" : "text-muted-foreground",
      )}>
        <Icon className="h-3.5 w-3.5" /> {title}
      </div>
      <div className="mt-3">{children}</div>
    </section>
  );
}

function Stat({ label, value, tone }: { label: string; value: string; tone?: "good" | "warn" | "bad" }) {
  return (
    <div className="rounded-xl border border-border bg-background p-3">
      <div className={cn(
        "font-display text-2xl font-bold",
        tone === "good" ? "text-emerald-600"
          : tone === "warn" ? "text-amber-600"
          : tone === "bad" ? "text-red-600"
          : "text-primary",
      )}>
        {value}
      </div>
      <div className="mt-0.5 text-[11px] text-muted-foreground">{label}</div>
    </div>
  );
}

function cellClass(m: number | null) {
  if (m == null) return "bg-muted/40 text-muted-foreground";
  if (m >= 70) return "bg-emerald-500/70 text-white";
  if (m >= 50) return "bg-amber-400/70 text-black";
  if (m >= 35) return "bg-orange-500/70 text-white";
  return "bg-red-500/70 text-white";
}

function csvCell(v: string) {
  const s = String(v ?? "");
  if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}
